import { useState, useEffect } from 'react'
import { Popup } from 'semantic-ui-react'
import { useSourceReliabilityTable } from '../../../../hooks/useDataQuality'
import { TimeWindow } from '../../../../types/dataQuality'
import LoadingSkeleton from '../../../common/LoadingSkeleton'
import ErrorDisplay from '../../../common/ErrorDisplay'
import SourceDetailView from './SourceDetailView'
import './DataSourcesTab.css'

interface DataSourcesTabProps {
  timeWindow: TimeWindow
  onTimeWindowChange?: (timeWindow: TimeWindow) => void
}

const COUNTER_ANIMATION_DURATION = 1500 // 1.5 seconds

const DataSourcesTab = ({ timeWindow, onTimeWindowChange }: DataSourcesTabProps) => {
  // Animation state for circular progress
  const [animatedValue, setAnimatedValue] = useState(0)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(7)
  const [selectedSource, setSelectedSource] = useState<any>(null)

  // Fetch source reliability table data (single API call for all sources)
  const { data: reliabilityData, isLoading, error, refetch } = useSourceReliabilityTable({
    time_window: timeWindow,
    limit: 100
  })

  // Local state for refetching
  const [isRefetching, setIsRefetching] = useState(false)

  // Refetch data when component mounts to ensure fresh data
  useEffect(() => {
    const fetchData = async () => {
      setIsRefetching(true)
      try {
        await refetch()
      } finally {
        setIsRefetching(false)
      }
    }
    fetchData()
  }, []) // Empty dependency array means this runs only on mount

  // Get aggregate values from API data summary
  const aggregateValues = reliabilityData?.summary || {} as any
  const currentScore = Math.round(aggregateValues.avg_score || 0)

  // Helper function to determine color class based on score
  const getMetricColorClass = (score: number): string => {
    if (score > 95) return 'reliable'
    if (score >= 90) return 'monitor'
    return 'unreliable'
  }

  // Helper function for duplicate metrics (inverse logic)
  const getDuplicateColorClass = (duplicateScore: number): string => {
    if (duplicateScore < 1) return 'reliable'
    if (duplicateScore >= 2 && duplicateScore < 4) return 'monitor'
    if (duplicateScore >= 4) return 'unreliable'
    // For values between 1% and 2% (1.x%), treat as reliable/green
    return 'reliable'
  }

  // Helper function to get color class (for popup)
  const getColorClass = (value: number, isDuplicate: boolean = false): string => {
    if (isDuplicate) {
      return getDuplicateColorClass(value)
    }
    return getMetricColorClass(value)
  }

  // Helper function to convert status class to color class for popup
  const getPopupColorClass = (value: number, isDuplicate: boolean = false): string => {
    const statusClass = getColorClass(value, isDuplicate)
    // Convert reliable/monitor/unreliable to green/yellow/red for popup CSS
    if (statusClass === 'reliable') return 'green'
    if (statusClass === 'monitor') return 'yellow'
    if (statusClass === 'unreliable') return 'red'
    return statusClass
  }

  // Create dynamic metrics from API data (5 metrics only like screenshot)
  const dynamicMetrics = [
    {
      id: 'completeness',
      label: 'Completeness',
      target: '>90%',
      value: (aggregateValues.avg_completeness || 0).toFixed(1),
      status: getMetricColorClass(aggregateValues.avg_completeness || 0)
    },
    {
      id: 'timeliness',
      label: 'Timeliness',
      target: '>90%',
      value: (aggregateValues.avg_timeliness || 0).toFixed(1),
      status: getMetricColorClass(aggregateValues.avg_timeliness || 0)
    },
    {
      id: 'quality',
      label: 'Quality',
      target: '>90%',
      value: (aggregateValues.avg_quality || 0).toFixed(1),
      status: getMetricColorClass(aggregateValues.avg_quality || 0)
    },
    {
      id: 'schema',
      label: 'Schema',
      target: '>90%',
      value: (aggregateValues.avg_schema || 0).toFixed(1),
      status: getMetricColorClass(aggregateValues.avg_schema || 0)
    },
    {
      id: 'duplicate',
      label: 'Duplicate',
      target: '<1%',
      value: (aggregateValues.avg_duplicates || 0).toFixed(1),
      status: getDuplicateColorClass(aggregateValues.avg_duplicates || 0)
    }
  ]

  // Animate the circular progress counter
  useEffect(() => {
    const targetValue = currentScore
    const increment = targetValue / (COUNTER_ANIMATION_DURATION / 16) // 60fps
    let current = 0

    const timer = setInterval(() => {
      current += increment
      if (current >= targetValue) {
        setAnimatedValue(targetValue)
        clearInterval(timer)
      } else {
        setAnimatedValue(Math.floor(current))
      }
    }, 16)

    return () => clearInterval(timer)
  }, [currentScore])

  // Handle loading state (include refetching state)
  if (isLoading || isRefetching) {
    return (
      <>
        <LoadingSkeleton variant="chart" count={1} />
        <div style={{ marginTop: '24px' }}>
          <LoadingSkeleton variant="metric" count={4} />
        </div>
      </>
    )
  }

  // Handle error state
  if (error) {
    return (
      <ErrorDisplay
        error={error}
        onRetry={refetch}
        title="Failed to Load Data Sources"
      />
    )
  }

  // Handle no data
  const sourceMetrics = reliabilityData?.sources || []
  if (sourceMetrics.length === 0) {
    return <ErrorDisplay error="No data sources available" title="No Data" />
  }

  // Pagination calculations
  const totalItems = sourceMetrics.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentItems = sourceMetrics.slice(startIndex, endIndex)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  return (
    <div className="data-sources-container">
      {/* Show detail view when a source is selected */}
      {selectedSource ? (
        <SourceDetailView 
          source={selectedSource} 
          onBack={() => setSelectedSource(null)} 
        />
      ) : (
        <>
          {/* Source Reliability Matrix Header */}
          <div className="sources-header">
            <div className="sources-header-left">
              <h2 className="sources-title">Source Reliability Matrix</h2>
            </div>
            <div className="sources-header-right">
              {onTimeWindowChange && (
                <select
                  className="time-window-dropdown"
                  value={timeWindow}
                  onChange={(e) => onTimeWindowChange(e.target.value as TimeWindow)}
                >
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="quarter">Last 90 Days</option>
                </select>
              )}
            </div>
          </div>

          {/* Aggregate Reliability Score Section */}
          <div className="overview-content">
        {/* Aggregate reliability score header */}
        <div className="overview-header">
          <div className="overview-header-left">
            <h3 className="overview-title">
              Aggregate reliability score
            </h3>
            <p className="overview-subtitle">
              Composite reliability index (0-100)
            </p>
          </div>
          <div className="reliability-legend">
            <div className="legend-item">
              <div className="legend-dot reliable"></div>
              <span className="legend-text">&gt; 95%</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot monitor"></div>
              <span className="legend-text">90-95%</span>
            </div>
            <div className="legend-item">
              <div className="legend-dot unreliable"></div>
              <span className="legend-text">&lt; 90%</span>
            </div>
          </div>
        </div>

        {/* Main content area with circular indicator and metrics */}
        <div className="overview-main-content">
          {/* Circular Progress Indicator */}
          <div className="circular-progress-container">
            <div className="circular-progress">
              <svg width="200" height="200">
                {/* Background circle */}
                <circle
                  cx="100"
                  cy="100"
                  r="85"
                  className="circle-background"
                />
                {/* Progress arc */}
                <circle
                  cx="100"
                  cy="100"
                  r="85"
                  className={`circle-progress ${getMetricColorClass(currentScore)}`}
                  strokeDasharray={`${(animatedValue / 100) * 534} 534`}
                />
              </svg>
              {/* Center text */}
              <div className="circle-text">
                <div className="circle-main-number">
                  {animatedValue}
                </div>
                <div className="circle-total-number">
                  / 100
                </div>
              </div>
            </div>
            <div className="circle-target-text">
              Target - 90/100
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="metrics-grid">
            {dynamicMetrics.map((metric) => (
              <div key={metric.id} className="metric-card">
                <div className="metric-info">
                  <div className="metric-label">
                    {metric.label}
                  </div>
                  <div className="metric-target">
                    Target {metric.target}
                  </div>
                </div>
                <div className={`metric-value ${metric.status}`}>
                  {metric.value}
                  {metric.label === "Duplicate" && (
                    <Popup
                      trigger={
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#6B7280"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="duplicate-info-icon"
                          style={{ marginLeft: '8px', cursor: 'pointer' }}
                        >
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="16" x2="12" y2="12"/>
                          <line x1="12" y1="8" x2="12.01" y2="8"/>
                        </svg>
                      }
                      position="right center"
                      on="hover"
                      className="duplicate-popup"
                    >
                      <div className="duplicate-popup-content">
                        <div className="duplicate-popup-section">
                          <div className="duplicate-popup-title">Duplicate</div>
                          <div className={`duplicate-popup-metric-value ${getPopupColorClass(parseFloat(metric.value), true)}`}>
                            {metric.value}%
                          </div>
                        </div>
                        <div className="duplicate-popup-section">
                          <div className="duplicate-popup-title">Benchmarks</div>
                          <div className="duplicate-benchmarks-list">
                            <div className="duplicate-benchmark-item">
                              <span className="duplicate-benchmark-dot green"></span>
                              <span className="duplicate-benchmark-label">&lt; 1%</span>
                            </div>
                            <div className="duplicate-benchmark-item">
                              <span className="duplicate-benchmark-dot yellow"></span>
                              <span className="duplicate-benchmark-label">2% to 4%</span>
                            </div>
                            <div className="duplicate-benchmark-item">
                              <span className="duplicate-benchmark-dot red"></span>
                              <span className="duplicate-benchmark-label">&gt;= 4%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Popup>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Source-Level Reliability Score Section */}
      <div className="reliability-score-section">
        <div className="section-header-with-legend">
          <div className="section-header-left">
            <h3 className="section-title">Source-Level Reliability Score ({aggregateValues.total_sources || sourceMetrics.length} sources)</h3>
            <p className="section-subtitle">Comprehensive health metrics by data source</p>
          </div>
          <div className="section-header-right">
            <div className="table-legend-inline">
              <div className="legend-item">
                <div className="legend-indicator reliable"></div>
                <span>&gt; 95%</span>
              </div>
              <div className="legend-item">
                <div className="legend-indicator monitor"></div>
                <span>90-95%</span>
              </div>
              <div className="legend-item">
                <div className="legend-indicator unreliable"></div>
                <span>&lt; 90%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Completeness (%)</th>
                <th>Timeliness (%)</th>
                <th>Quality (%)</th>
                <th>Schema (%)</th>
                <th>Duplicate (%)</th>
                <th>Score</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((source) => {
                const completenessClass = getMetricColorClass(source.completeness)
                const timelinessClass = getMetricColorClass(source.timeliness)
                const qualityClass = getMetricColorClass(source.quality)
                const schemaClass = getMetricColorClass(source.schema)
                const duplicatesClass = getDuplicateColorClass(source.duplicates)
                const scoreClass = getMetricColorClass(source.score)
                
                // Check if source has valid data (at least one metric or source_id)
                const hasData = source.source_id && (
                  source.completeness > 0 || 
                  source.timeliness > 0 || 
                  source.quality > 0 || 
                  source.schema > 0 || 
                  source.score > 0
                )

                return (
                  <tr key={source.source_id}>
                    <td className="source-name-cell">{source.source_name}</td>
                    <td>
                      <div className={`table-metric-badge ${completenessClass}`}>
                        <span className="metric-number">{Math.round(source.completeness)}</span>
                        <span className="metric-text">%</span>
                      </div>
                    </td>
                    <td>
                      <div className={`table-metric-badge ${timelinessClass}`}>
                        <span className="metric-number">{Math.round(source.timeliness)}</span>
                        <span className="metric-text">%</span>
                      </div>
                    </td>
                    <td>
                      <div className={`table-metric-badge ${qualityClass}`}>
                        <span className="metric-number">{Math.round(source.quality)}</span>
                        <span className="metric-text">%</span>
                      </div>
                    </td>
                    <td>
                      <div className={`table-metric-badge ${schemaClass}`}>
                        <span className="metric-number">{Math.round(source.schema)}</span>
                        <span className="metric-text">%</span>
                      </div>
                    </td>
                    <td>
                      <div className={`table-metric-badge ${duplicatesClass}`}>
                        <span className="metric-number">{Math.round(source.duplicates)}</span>
                        <span className="metric-text">%</span>
                      </div>
                    </td>
                    <td>
                      <div className={`table-score-badge ${scoreClass}`}>
                        <span className="score-number">{Math.round(source.score)}</span>
                      </div>
                    </td>
                    <td>
                      <button
                        className="view-details-btn"
                        onClick={() => hasData && setSelectedSource(source)}
                        disabled={!hasData}
                        style={{ 
                          cursor: hasData ? 'pointer' : 'not-allowed',
                          opacity: hasData ? 1 : 0.5
                        }}
                        title={hasData ? '' : 'No data available'}
                      >
                        View details →
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="pagination-controls">
            <button 
              onClick={handlePrevPage} 
              className="pagination-btn"
              disabled={currentPage === 1}
            >
              ← Previous
            </button>
            
            <div className="pagination-numbers">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                >
                  {page}
                </button>
              ))}
            </div>
            
            <button 
              onClick={handleNextPage} 
              className="pagination-btn"
              disabled={currentPage === totalPages}
            >
              Next →
            </button>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  )
}

export default DataSourcesTab
